import { useState, useEffect } from "react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Edit, Trash, Users, User, Phone, Mail, Lock, Eye, EyeOff, RefreshCw } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { USERCREATE, USERSLIST_URL, ROLES_URL } from "../../constants/api";
import { Select } from "antd";

// Role types whose access comes from their assigned role. 'super_admin'
// bypasses permissions entirely; 'third_party' uses the portal rules.
const NEEDS_ROLE = ['user', 'admin'];

const UserList = () => {
    const { create, update, fetch } = useApiMethods();
    const [userListsData, setUserListsData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [rolesList, setRolesList] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    // form state
    const [formData, setFormData] = useState({
        id: null,
        emp_code: "",
        user_first_name: "",
        user_last_name: "",
        user_name: "",
        phonenumber: '',
        email: '',
        password: '',
        notes: "",
        is_active: true,
        role_type: "user",
        assigned_role_id: null,
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetch(ROLES_URL).then(res => {
            if (res?.status === "success") setRolesList(res.data || []);
        });
    }, []);

    // reset form & state
    const resetForm = () => {
        setFormData({
            id: null,
            emp_code: "",
            user_first_name: "",
            user_last_name: "",
            phonenumber: '',
            email: '',
            password: '',
            notes: "",
            is_active: true,
            role_type: "user",
            assigned_role_id: null,
        });
        setErrors({});
        setShowPassword(false);
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.user_first_name.trim()) {
            newErrors.user_first_name = "First name is required";
        }

        if (!formData.user_name || !formData.user_name.trim()) {
            newErrors.user_name = "Username is required";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email is invalid";
        }

        if (!formData.phonenumber.trim()) {
            newErrors.phonenumber = "Phone number is required";
        } else if (!/^[6-9]\d{9}$/.test(formData.phonenumber.replace(/\D/g, ''))) {
            newErrors.phonenumber = "Please enter a valid 10-digit Indian mobile number";
        }

        // Must match the backend rule (PASSWORD_REGEX): min 12 chars with
        // 1 uppercase, 1 lowercase, 1 number and 1 special character.
        const STRONG_PW = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[a-zA-Z0-9\S]{12,}$/;
        if (!editMode && !formData.password) {
            newErrors.password = "Password is required";
        } else if (formData.password && !STRONG_PW.test(formData.password)) {
            newErrors.password = "Password must be at least 12 characters and contain 1 uppercase, 1 lowercase, 1 number, and 1 special character";
        }

        // Admins are now governed by their assigned role like a user — only
        // super_admin still bypasses permissions. So an admin without a role
        // would land with no access at all, which is why the role is required
        // for both. See FULL_ACCESS_ROLE_TYPES in the backend.
        if (NEEDS_ROLE.includes(formData.role_type) && !formData.assigned_role_id) {
            newErrors.assigned_role_id = "Role is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle input change
    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const next = { ...prev, [field]: value };
            // Clear the role only for the types that genuinely ignore it.
            // It used to be cleared for anything other than 'user', which
            // silently stripped the role off an admin the moment their type was
            // set — leaving an account with no permissions at all.
            if (field === 'role_type' && !NEEDS_ROLE.includes(value)) {
                next.assigned_role_id = null;
            }
            return next;
        });
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
        if (field === 'role_type' && !NEEDS_ROLE.includes(value) && errors.assigned_role_id) {
            setErrors(prev => ({ ...prev, assigned_role_id: "" }));
        }
    };

    // Format phone number
    const handlePhoneChange = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedDigits = numbers.slice(0, 10);

        let formattedValue = limitedDigits;

        handleInputChange('phonenumber', formattedValue);
    };
    // And update placeholder to: "98765-43210"

    // table columns
    const userListColumns = [
        {
            accessor: "login_fname",
            Header: "First Name",
            sortable: true,
            filterable: true,
        },
        {
            accessor: "login_lname",
            Header: "Last Name",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value.length !== 0 ? value : "-"
        },
        {
            accessor: "login_email_address",
            Header: "Email",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => (
                <a
                    href={`mailto:${value}`}
                    className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                >
                    {value}
                </a>
            )
        },
        {
            accessor: "login_phone_number",
            Header: "Phone",
            sortable: false,
            Cell: ({ value }) => (
                <a
                    href={`tel:${value}`}
                    className="text-gray-600 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 text-sm"
                >
                    {value}
                </a>
            )
        },
        {
            accessor: "role_type",
            Header: "User Type",
            sortable: true,
            Cell: ({ value }) => (
                <span className={cn(
                    "px-2 py-1 text-xs font-medium rounded-full capitalize",
                    value === "super_admin" ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                    : value === "admin"     ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300"
                )}>
                    {value?.replace('_', ' ') || 'user'}
                </span>
            ),
        },
        {
            accessor: "role_name",
            Header: "Role",
            sortable: false,
            Cell: ({ value }) => value
                ? <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{value}</span>
                : <span className="text-xs text-gray-400">—</span>,
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            accessor: "action",
            Header: "Actions",
            sortable: false,
            fixed: true,
            align: 'right',
            Cell: ({ row }) => {
                const handleOpenDialog = () => {
                    setEditMode(true);
                    setEditId(row?.emp_code);
                    setFormData({
                        id: row?.id || null,
                        emp_code: row?.emp_code || "",
                        user_first_name: row?.login_fname || "",
                        user_last_name: row?.login_lname || "",
                        phonenumber: row?.login_phone_number || '',
                        email: row?.login_email_address || '',
                        user_name: row?.login_name || '',
                        password: '', // Don't show existing password
                        notes: row?.notes || "",
                        is_active: row?.is_active ?? true,
                        role_type: row?.role_type || "user",
                        assigned_role_id: row?.assigned_role_id || null,
                    });
                    setOpenDialog(true);
                };

                const handleDeleteClick = (row) => {
                    setSelectedRow(row);
                    setIsDeleteConfirmOpen(true);
                };

                return (
                    <div className="flex items-center gap-2">
                        {/* Edit Button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700"
                            onClick={handleOpenDialog}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>

                        {/* Delete Button (only if row is draft) */}
                        {row?.is_draft && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                onClick={() => handleDeleteClick(row)}
                            >
                                <Trash className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ];

    // save as draft
    const handleDraft = async () => {
        if (!validateForm()) {
            ToastMsg("error", "Please fix the errors in the form");
            return;
        }

        try {
            setLoading(true);
            const payload = { ...formData, is_draft: true };
            const response = await create(USERSLIST_URL, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "User saved to draft successfully");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to save draft");
            }
        } catch (error) {
            console.error("Error saving draft:", error);
            ToastMsg("error", "Failed to save draft");
        } finally {
            setLoading(false);
        }
    };

    const handleDraftDelete = async () => {
        try {
            setLoading(true);
            if (!selectedRow) return;

            const payload = {
                ...selectedRow,
                draft_delete: true
            }
            const response = await update(USERSLIST_URL, payload, false, setLoading);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Draft deleted successfully");
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to delete draft");
            }
        } catch (error) {
            console.error("Error deleting draft:", error);
            ToastMsg("error", "Failed to delete draft");
        } finally {
            setLoading(false);
            setSelectedRow(null);
            setIsDeleteConfirmOpen(false);
        }
    };

    // save or update
    const handleSave = async () => {
        if (!validateForm()) {
            ToastMsg("error", "Please fix the errors in the form");
            return;
        }

        try {
            setLoading(true);
            let payload = {
                ...formData,
                login_fname: formData.user_first_name,
                login_lname: formData.user_last_name,
                login_phone_number: formData.phonenumber,
                login_email_address: formData.email,
                user_name: formData.user_name,
                is_draft: false,
                assigned_role_id: formData.assigned_role_id || null,
            };
            let response;

            response = await create(USERCREATE, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || `User ${editMode ? 'updated' : 'created'} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? 'update' : 'create'} user`);
            }
        } catch (error) {
            console.error("Error saving user:", error);
            ToastMsg("error", `Failed to ${editMode ? 'update' : 'create'} user`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Users</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage system users and their access permissions</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setServerTrigger(p => !p)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </button>
                    <button
                        onClick={() => setOpenDialog(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                    >
                        Add User
                    </button>
                </div>
            </div>

            <Reactable
                columns={userListColumns}
                data={userListsData}
                setData={setUserListsData}
                setTotalCount={setTotalRecords}
                url={USERSLIST_URL}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={"xs"}
                isCSVRequired={true}
                isPDFRequired={false}
                isDraftRequired={true}
                isDateRequired={false}
            />

            {/* Add/Edit Modal */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode ? "Edit User" : "Add New User"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Create"}
                draftLabel="Save as Draft"
                editMode={editMode}
                loading={loading}
                width="800px"
            >
                <form className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className=" space-y-2">
                            {/* Name Fields */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label
                                        htmlFor="user_first_name"
                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                    >
                                        First Name <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="user_first_name"
                                        value={formData.user_first_name}
                                        onChange={(e) => handleInputChange('user_first_name', e.target.value)}
                                        placeholder="Enter first name"
                                        required
                                        disabled={loading}
                                        className={cn(
                                            "w-full rounded-lg transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                            "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400",
                                            errors.user_first_name && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                                        )}
                                    />
                                    {errors.user_first_name && (
                                        <p className="text-red-500 text-xs">{errors.user_first_name}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label
                                        htmlFor="user_last_name"
                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                    >
                                        Last Name
                                    </label>
                                    <Input
                                        id="user_last_name"
                                        value={formData.user_last_name}
                                        onChange={(e) => handleInputChange('user_last_name', e.target.value)}
                                        placeholder="Enter last name"
                                        disabled={loading}
                                        className={cn(
                                            "w-full rounded-lg transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                            "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400",
                                            errors.user_last_name && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                                        )}
                                    />
                                    {errors.user_last_name && (
                                        <p className="text-red-500 text-xs">{errors.user_last_name}</p>
                                    )}
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label
                                        htmlFor="email"
                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                    >
                                        <Mail className="h-3 w-3" />
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        placeholder="Enter email address"
                                        required
                                        disabled={loading}
                                        className={cn(
                                            "w-full rounded-lg transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                            "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400",
                                            errors.email && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                                        )}
                                    />
                                    {errors.email && (
                                        <p className="text-red-500 text-xs">{errors.email}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label
                                        htmlFor="phonenumber"
                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                    >
                                        <Phone className="h-3 w-3" />
                                        Phone Number <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="phonenumber"
                                        value={formData.phonenumber}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        placeholder="98765 43210"
                                        required
                                        disabled={loading}
                                        className={cn(
                                            "w-full rounded-lg transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                            "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400",
                                            errors.phonenumber && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                                        )}
                                    />
                                    {errors.phonenumber && (
                                        <p className="text-red-500 text-xs">{errors.phonenumber}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label
                                        htmlFor="user_first_name"
                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                    >
                                        User Name <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="user_name"
                                        value={formData.user_name}
                                        onChange={(e) => handleInputChange('user_name', e.target.value)}
                                        placeholder="Enter User name"
                                        required
                                        disabled={loading}
                                        className={cn(
                                            "w-full rounded-lg transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                            "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                            "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                                            "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400",
                                            errors.user_name && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                                        )}
                                    />
                                    {errors.user_name && (
                                        <p className="text-red-500 text-xs">{errors.user_name}</p>
                                    )}
                                </div>
                                {/* Password */}
                                <div className="space-y-2">
                                    <label
                                        htmlFor="password"
                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                    >
                                        <Lock className="h-3 w-3" />
                                        Password {!editMode && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={formData.password}
                                            onChange={(e) => handleInputChange('password', e.target.value)}
                                            placeholder={editMode ? "Leave blank to keep current password" : "Enter password"}
                                            required={!editMode}
                                            disabled={loading}
                                            className={cn(
                                                "w-full rounded-lg transition-colors duration-200 pr-10",
                                                "border-gray-300 dark:border-gray-600",
                                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                                "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400",
                                                errors.password && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                                            )}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="text-red-500 text-xs">{errors.password}</p>
                                    )}
                                    {editMode && (
                                        <p className="text-gray-500 text-xs">Leave password blank to keep current password</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className=" space-y-2">
                            {/* Role Type */}
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    User Type <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-3">
                                    {[
                                        { value: 'user',        label: 'Is User' },
                                        { value: 'admin',       label: 'Is Admin' },
                                        { value: 'super_admin', label: 'Is Super Admin' },
                                    ].map(opt => (
                                        <label key={opt.value}
                                            className={cn(
                                                "flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs font-medium transition-colors flex-1 justify-center",
                                                formData.role_type === opt.value
                                                    ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-500"
                                                    : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500"
                                            )}>
                                            <input
                                                type="radio"
                                                name="role_type"
                                                value={opt.value}
                                                checked={formData.role_type === opt.value}
                                                onChange={() => handleInputChange('role_type', opt.value)}
                                                className="accent-blue-600"
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Assigned Role — needed by 'user' and 'admin'; only super_admin bypasses */}
                            {NEEDS_ROLE.includes(formData.role_type) ? (
                                <div className="space-y-2">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Role <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={formData.assigned_role_id || undefined}
                                        onChange={val => handleInputChange('assigned_role_id', val || null)}
                                        placeholder="Select a role"
                                        disabled={loading}
                                        className="w-full"
                                        status={errors.assigned_role_id ? 'error' : ''}
                                        options={rolesList.filter(r => r.is_active).map(r => ({ value: r.id, label: r.name }))}
                                    />
                                    <p className="text-gray-500 text-xs">
                                        The selected role's ticked pages become this user's access.
                                        {formData.role_type === 'admin' &&
                                            ' Admins can always manage users and roles, whatever the role grants.'}
                                    </p>
                                    {errors.assigned_role_id && (
                                        <p className="text-red-500 text-xs">{errors.assigned_role_id}</p>
                                    )}
                                </div>
                            ) : (
                                <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Super admins have full access to every page — no role needed.
                                    </p>
                                </div>
                            )}

                            {/* Active Status */}
                            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                <label
                                    htmlFor="is_active"
                                    className="text-xs font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Account Status
                                </label>
                                <div className="flex items-center gap-3">
                                    <span className={cn(
                                        "text-xs font-medium",
                                        formData.is_active
                                            ? "text-green-600 dark:text-green-400"
                                            : "text-gray-500 dark:text-gray-400"
                                    )}>
                                        {formData.is_active ? "Active" : "Inactive"}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange('is_active', !formData.is_active)}
                                        className={cn(
                                            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                            formData.is_active ? "bg-primary" : "bg-gray-300 dark:bg-gray-600",
                                            loading && !editMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                        )}
                                        disabled={loading && !editMode}
                                    >
                                        <span
                                            className={cn(
                                                "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                                formData.is_active ? "translate-x-4" : "translate-x-1"
                                            )}
                                        />
                                    </button>
                                </div>
                            </div>

                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="text-red-500">*</span> indicates required field
                            </p>
                        </div>
                    </div>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDraftDelete}
                title="Delete Draft User"
                description="Are you sure you want to delete this draft user? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default UserList;